import { action } from '@ember/object';
import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Command from 'editor/models/command';
import { CompositeCommand } from 'editor/models/command';
import isEqual from 'lodash.isequal';

export default class UndoManagerService extends Service {
  undoStack: Command<any>[] = [];
  redoStack: Command<any>[] = [];

  @tracked dirtyPoint = [];
  @tracked undoLevel = 0;
  @tracked redoLevel = 0;

  constructor(properties?: object) {
    super(properties);
    const isMac = /Mac|iPhone|iPad/.test(navigator.platform);

    function isEditable(el: Element | null): boolean {
      if (!el) return false;
      if ((el as HTMLElement).isContentEditable) return true;

      // Textareas and text-like inputs
      if (el instanceof HTMLTextAreaElement) {
        return true;
      }
      if (el instanceof HTMLInputElement) {
        const nonText = new Set([
          'button','checkbox','radio','range','color','file','image','submit',
          'reset','hidden','date','time','month','week','datetime-local','number'
        ]);
        return !nonText.has((el.type || 'text').toLowerCase());
      }

      // Common web editors
      if (el.closest?.('.monaco-editor, .CodeMirror, [role="textbox"]')) return true;
      return false;
    }

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.isComposing) return; // don't interfere with IME
        const k = e.key.toLowerCase();
        const hasCtrl = e.ctrlKey || e.metaKey;

        const isUndo = hasCtrl && k === 'z' && !e.shiftKey;
        const isRedo =
          (isMac && e.metaKey && k === 'z' && e.shiftKey) ||         // Cmd+Shift+Z
          (!isMac && ((e.ctrlKey && k === 'y') || (e.ctrlKey && k === 'z' && e.shiftKey)));

        if (!(isUndo || isRedo)) return;

        // If focus is NOT in something editable, block the browser undo/redo
        if (!isEditable(document.activeElement)) {
          e.preventDefault();
          e.stopPropagation();
          if (isUndo) this.undo();
          else this.redo();
        }
        // else: let the browser/editor handle it
      },
      { capture: true } // run early & reliably
    );
  }

  get isDirty() {
    // this.undoLevel is a hack to check if the undo stack is dirty.
    // it's tracked, so when it changes, we call this
    // we could instead track the undo stack, but since its the array reference that changes
    // we'd have to assign it to a new array each time (undoStack.push wouldn't trigger a change)
    this.undoLevel;

    const dirty = isEqual(this.dirtyPoint, this.undoStack);
    return !dirty;
  }

  // Resets everything
  reset() {
    this.undoStack = [];
    this.redoStack = [];
    this.undoLevel = 0;
    this.redoLevel = 0;
    this.dirtyPoint = [];
  }

  // Resets after a save but still allows undo
  resetDirty() {
    this.dirtyPoint = [...this.undoStack];
  }

  get canUndo() {
    return this.undoLevel > 0;
  }

  get canRedo() {
    return this.redoLevel > 0;
  }

  @action
  undo() {
    const command = this.undoStack.pop();
    if (command) {
      this.undoLevel--;
      command.undo();
      this.redoStack.push(command);
      this.redoLevel++;
    }
  }

  @action
  redo() {
    const command = this.redoStack.pop();
    if (command) {
      this.redoLevel--;
      command.perform();
      this.undoStack.push(command);
      this.undoLevel++;
    }
  }

  executeCommand(command: Command<any>) {
    this.redoStack = [];
    this.redoLevel = 0;
    const result = command.perform();
    this.undoStack.push(command);
    this.undoLevel++;
    return result;
  }

  appendCommand(command: Command<any>, standAloneCommand = true) {
    const lastCommand = this.undoStack.pop();
    if (lastCommand) {
      const compositeCommand = new CompositeCommand(lastCommand.name, [
        lastCommand,
        command,
      ]);
      command.perform();
      this.undoStack.push(compositeCommand);
    } else {
      if (standAloneCommand) {
        this.executeCommand(command);
      }
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'undo-manager': UndoManagerService;
  }
}